'use client';
import { useId as useNewsletterId } from 'react';
import { CaptchaRequestMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import React from 'react';

export function Newsletter() {
  const seoNewsletterId = useNewsletterId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <>
      <section
        className={cn(
          'relative z-10 bg-newsletter bg-cover bg-center px-5 py-[50px] md:rounded-lg md:border-2 md:p-4 md:px-10 md:py-[120px]',
          'ea-homepage-newsletter',
        )}
      >
        <form
          className={cn(
            'flex w-full flex-col items-start justify-start gap-6 py-2 md:max-w-[75%] lg:max-w-[50%]',
            'ea-homepage-newsletter__form',
          )}
          onSubmit={handleSubmit}
        >
<CaptchaRequestMessage error={subscribeMutation.error} />
          {/* icon + heading */}
          <div
            className={cn(
              'flex flex-col items-start justify-start gap-10 md:flex-row md:pr-7',
              'ea-homepage-newsletter__form-icon-email',
            )}
          >
            <Image
              alt="newsletterIcon"
              width={0}
              height={0}
              sizes="100vw"
              className="h-[80px] w-[80px]"
              src={'/images/newsletterIcon.svg'}
            />
            <div className={cn('flex flex-col gap-2', 'ea-homepage-newsletter__form-title')}>
              <h2
                data-ea-visual-tag="h4"
                className={cn(
                  'max-w-sm text-2xl font-bold',
                  'ea-homepage-newsletter__form-title-main',
                )}
              >
                {t('text610')}
              </h2>
              <p className={cn('text-sm text-black', 'ea-homepage-newsletter__form-title-sub')}>
                {t('text609')}
              </p>
            </div>
          </div>
          {/* input */}
          <div
            className={cn(
              'flex flex-col justify-start space-y-3',
              'ea-homepage-newsletter__form-item-email',
            )}
          >
            <div className="flex w-full flex-col items-center justify-start gap-5 md:flex-row">
              <Input
                aria-label={systemLabels('email')}
                id={`newsletter-email-${seoNewsletterId}`}
                type="email"
                name="email"
                placeholder={t('text183')}
                onChange={(e) => setEmail(e.target.value)}
                className={{
                  wrapper: cn(
                    'rounded-full border bg-white pr-1 text-muted-foreground focus-within:border-none focus:border-none focus-visible:border-none',
                    'ea-homepage-newsletter__form-item-email-input',
                  ),
                  main: 'rounded-full bg-white focus-within:border-none focus:border-none focus-visible:border-none',
                }}
              />
              <Button
                type="submit"
                className={cn(
                  'h-10 w-full rounded-full border-none bg-[#71B036] px-3 text-primary-foreground md:max-w-fit',
                  'ea-homepage-newsletter__form-item-email-submit-button',
                )}
                disabled={email.length === 0 || !checked || isSubscribed}
                isLoading={subscribeMutation.isPending}
              >
                {t('text182')}
              </Button>
            </div>
            {isSubscribed && <span className={cn('text-success')}>{t('text234')}</span>}
            <div
              className={cn(
                'flex flex-row items-center space-x-2',
                'ea-homepage-newsletter__form-item-gdpr',
              )}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={() => setChecked(!checked)}
                id={`newsletter-gdpr-${seoNewsletterId}`}
                aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
                className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
                required
              />
              <Label
                htmlFor={`newsletter-gdpr-${seoNewsletterId}`}
                className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-label')}
              >
                <span className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-span')}>
                  {t('text264')}&nbsp;
                  <a
                    href={t('text391')}
                    className={cn(
                      'text-primary',
                      'ea-homepage-newsletter__form-item-gdpr-checkbox-link1',
                    )}
                  >
                    {t('text265')}
                  </a>{' '}
                  <a
                    href={t('text392')}
                    className={cn(
                      'text-primary',
                      'ea-homepage-newsletter__form-item-gdpr-checkbox-link2',
                    )}
                  >
                    {t('text436')}
                  </a>
                  .
                </span>
              </Label>
            </div>
          </div>
        </form>
        {/* gradient */}
        <div className="newsletterGradient absolute left-0 top-0 -z-10 h-full w-full"></div>
      </section>
    </>
  );
}
